import { Outlet, useNavigate, useParams, useLocation } from 'react-router-dom'
import { LogOut, ChevronDown, LayoutDashboard, BookOpen } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useProjects } from '../hooks/useProjects'
import { LlamigoWidget } from './LlamigoWidget'

function isStaff(email: string | null | undefined): boolean {
  return Boolean(email && email.endsWith('@llamassist.ai'))
}

export function Layout() {
  const { user, logout } = useAuth()
  const { projects } = useProjects()
  const { projectSlug } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const staff = isStaff(user?.email)
  const currentProject = projects.find((p) => p.short_uuid === projectSlug)
  const onAdminView = location.pathname.startsWith('/admin')

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Left: Brand + nav */}
            <div className="flex items-center gap-4">
              <span
                className="text-lg font-bold text-brand-500 cursor-pointer"
                onClick={() => navigate(staff ? '/admin' : '/')}
              >
                LlamAssist
              </span>

              {/* Staff admin tab */}
              {staff && (
                <button
                  onClick={() => navigate('/admin')}
                  className={`flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-md font-medium transition-colors ${
                    onAdminView
                      ? 'bg-brand-50 text-brand-600'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <LayoutDashboard className="w-4 h-4" />
                  Queue
                </button>
              )}

              {/* Client: Help Center link */}
              {!staff && (
                <button
                  onClick={() => navigate('/help')}
                  className={`flex items-center gap-1.5 text-sm px-2.5 py-1 rounded-md font-medium transition-colors ${
                    location.pathname.startsWith('/help')
                      ? 'bg-brand-50 text-brand-600'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <BookOpen className="w-4 h-4" />
                  Help
                </button>
              )}

              {/* Client project switcher */}
              {!staff && currentProject && projects.length > 0 && (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center gap-1 text-sm font-medium text-gray-700 hover:text-gray-900 px-2 py-1 rounded-md hover:bg-gray-50"
                  >
                    {currentProject.name}
                    {projects.length > 1 && <ChevronDown className="w-4 h-4" />}
                  </button>
                  {dropdownOpen && projects.length > 1 && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[180px]">
                      {projects.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => {
                            navigate(`/${p.short_uuid}`)
                            setDropdownOpen(false)
                          }}
                          className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                            p.short_uuid === projectSlug ? 'text-brand-500 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {p.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right: User role badge + email + logout */}
            <div className="flex items-center gap-3">
              {staff && (
                <span className="text-xs font-medium text-brand-600 bg-brand-50 border border-brand-200 rounded-full px-2 py-0.5 hidden sm:inline">
                  Staff
                </span>
              )}
              <span className="text-sm text-gray-500 hidden sm:block">{user?.email}</span>
              <button
                onClick={logout}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Outlet />
      </main>

      {/* Llamigo floating widget — visible on all authenticated pages */}
      <LlamigoWidget />
    </div>
  )
}
